import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { colorsMapping, getFadeColor } from '@repo/toolkit/colors'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { AddUserIcon, EuroIcon, FormIcon, PharmacyIcon, UploadIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import { API } from '../../../api/apis.config'
import { assignEnrollmentsToAdviser } from '../../../api/enrollments/assign-enrollments-to-adviser'
import {
  getEnrollmentsDistribution,
  getEnrollmentsStatusesDistribution
} from '../../../api/enrollments/get-enrollments-distribution'
import CountryFlag from '../../../components/elements/country-flag'
import CseIdentifierTooltip from '../../../components/elements/cse-identifier-tooltip'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import InformationToolTip from '../../../components/elements/information-tooltip'
import UserNavBar from '../../../components/elements/nav-bar'
import AssignEnrollmentForm from '../../../components/pages/enrollments/assign-enrollment-form'
import { EnrollmentsFilter } from '../../../components/pages/enrollments/enrollment-filter'
import { Paths } from '../../../lib/constants/path'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import {
  ApplicationStatusEnum,
  applicationStatusEnumMapping
} from '../../../lib/types/enums/enrollment/application-status.enum'
import {
  MedicalConditionEnum,
  medicalConditionIndicatorEnumMapping
} from '../../../lib/types/enums/enrollment/medical-condition-enum'
import { PaymentStatusEnumMapping } from '../../../lib/types/enums/enrollment/payment-status.enum'
import { origineEnumMapping } from '../../../lib/types/enums/information-requests/origin.enum'
import {
  enrollmentListFields,
  EnrollmentListType
} from '../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentFilter } from '../../../lib/types/models/enrollment/enrollment-filter'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformEnrollmentsFiltersToUrl,
  transformUrlToEnrollmentFilters
} from '../../../lib/utils/filters-mapping/enrollment'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

const displayStatuses: ApplicationStatusEnum[] = [
  ApplicationStatusEnum.PENDING_LEC_CONFIRMATION,
  ApplicationStatusEnum.PENDING_CSE_VALIDATION,
  ApplicationStatusEnum.REJECTED_BY_CSE,
  ApplicationStatusEnum.PENDING_CLIENT_VALIDATION,
  ApplicationStatusEnum.CANCELLED_BEFORE_CONFIRMATION,
  ApplicationStatusEnum.TO_BE_CONTACTED
]

const initialFilters: EnrollmentFilter = {
  statuses: [
    ApplicationStatusEnum.PENDING_LEC_CONFIRMATION,
    ApplicationStatusEnum.PENDING_CSE_VALIDATION,
    ApplicationStatusEnum.REJECTED_BY_CSE,
    ApplicationStatusEnum.PENDING_CLIENT_VALIDATION,
    ApplicationStatusEnum.TO_BE_CONTACTED
  ]
}

const EnrollmentsListPage = () => {
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { downloadDocuments } = useDownloadDocuments()
  const tableRef = useRef<DynamicTableRef<EnrollmentListType>>(null)
  const { navigateWithEvent } = useNavigator()

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'filters' | 'assign',
    EnrollmentListType[]
  >(['filters', 'assign'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<EnrollmentFilter>({
      initialFilters,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformEnrollmentsFiltersToUrl,
      transformUrlToFilter: transformUrlToEnrollmentFilters
    })

  const { mutateAsync } = useMutation({
    mutationFn: assignEnrollmentsToAdviser,
    onError: showAxiosError
  })

  const { data: statuses } = useQuery({
    queryKey: ['enrollment-statuses', selectedFilter],
    queryFn: () => getEnrollmentsStatusesDistribution(selectedFilter)
  })

  const { data: enrollmentsDistribution } = useQuery({
    initialData: { normalIndividuals: 0, cse: 0 },
    queryKey: ['enrollment-distribution', selectedFilter],
    queryFn: () => getEnrollmentsDistribution(selectedFilter)
  })

  const columns: Column<EnrollmentListType>[] = [
    {
      accessorKey: 'childLastName',
      header: 'NOM Prénom',
      enableSorting: true,
      width: 110,
      cellClassName: 'whitespace-normal',
      cell: ({ row: { original } }) =>
        toFullName(original.childFirstName, original.childLastName, {
          options: { mode: 'lastname-firstname' }
        })
    },
    {
      accessorKey: 'childBirthDate',
      header: 'Âge',
      sortDescFirst: true,
      invertSorting: true,
      centerCell: true,
      enableSorting: true,
      cell: ({ row: { original } }) => {
        const age = EnrollmentProperties.getAgeAtDeparture(original)
        if (age === null) return '-'
        if (original.isAgeCompliant !== false) return age
        return (
          <div className="w-7 h-7 min-w-7 shrink-0 rounded-full bg-red-light inline-flex items-center justify-center">
            <Typography variant="label-semibold" color={tailwindColors.red.DEFAULT}>
              {age}
            </Typography>
          </div>
        )
      }
    },
    {
      accessorKey: 'medicalLevel',
      header: 'Santé',
      centerCell: true,
      cell: ({ row }) => {
        const medicalLevel = row.original.medicalLevel
        if (medicalLevel === MedicalConditionEnum.RAS) return '-'
        const indicator = medicalConditionIndicatorEnumMapping[medicalLevel] as {
          label: string
          variant: 'success' | 'warning' | 'danger'
        }
        return (
          <PharmacyIcon
            className="!size-5 shrink-0"
            color={colorsMapping[indicator.variant].text}
            color2={colorsMapping[indicator.variant].hover}
          />
        )
      }
    },
    {
      accessorKey: 'createdAt',
      header: `Date et heure d'inscription`,
      enableSorting: true,
      cellClassName: 'whitespace-normal',
      cell: ({ row }) => (
        <Badge
          variant="grey"
          size="flexible"
          text={DateFormatter.ddMMYYYY_HHMM(row.original.createdAt)}
        />
      )
    },
    {
      accessorKey: EnrollmentProperties.getEnrollmentCountry().key,
      header: 'Pays',
      centerCell: true,
      enableSorting: true,
      cell: ({ row }) => {
        const value = EnrollmentProperties.getEnrollmentCountry(row.original).value
        if (!value) return '-'
        return <CountryFlag code={value} />
      }
    },
    {
      accessorKey: EnrollmentProperties.getEnrollmentDates().key,
      header: 'Dates de séjour',
      enableSorting: true,
      cell: ({ row }) => {
        const value = EnrollmentProperties.getEnrollmentDates(row.original).value
        if (!value) return '-'
        const { startDate, endDate, color } = value
        return (
          <Badge
            size="flexible"
            text={`${startDate} -\n${endDate}`}
            textClassName="whitespace-pre-line"
            color={getFadeColor(color)}
            textColor={color}
          />
        )
      }
    },
    {
      accessorKey: EnrollmentProperties.getEnrollmentFormula().key,
      header: 'Formule',
      enableSorting: true,
      width: 90,
      cellClassName: 'whitespace-normal',
      cell: ({ row }) => EnrollmentProperties.getEnrollmentFormula(row.original).value ?? '-'
    },
    {
      accessorKey: EnrollmentProperties.getEnrollmentMainOption().key,
      header: 'Thème',
      enableSorting: true,
      width: 90,
      cellClassName: 'whitespace-normal',
      cell: ({ row }) => EnrollmentProperties.getEnrollmentMainOption(row.original).value ?? '-'
    },
    {
      accessorKey: EnrollmentProperties.getEnrollmentOptions().key,
      header: 'Option facultative',
      width: 90,
      cellClassName: 'whitespace-normal',
      cell: ({ row }) => EnrollmentProperties.getEnrollmentOptions(row.original).value
    },

    {
      accessorKey: EnrollmentProperties.getEnrollmentTripPlace().key,
      header: 'Ville de départ',
      enableSorting: true,
      width: 80,
      cellClassName: 'whitespace-normal',
      cell: ({ row }) => EnrollmentProperties.getEnrollmentTripPlace(row.original).value ?? '-'
    },
    {
      accessorKey: EnrollmentProperties.getEnrollmentTransport().key,
      header: 'Transport',
      enableSorting: true,
      width: 100,
      cellClassName: 'whitespace-normal',
      cell: ({ row }) =>
        EnrollmentProperties.getEnrollmentTransport(row.original, { withPlaneDepartureCity: true })
          .value ?? '-'
    },
    {
      accessorKey: 'paymentStatus',
      header: 'Statut paiement',
      enableSorting: true,
      centerCell: true,
      cell: ({ row }) => {
        const { label, variant } = PaymentStatusEnumMapping[row.original.paymentStatus]
        return (
          <InformationToolTip
            icon={
              <Badge
                variant={variant}
                icon={
                  <EuroIcon className="!size-5" color={colorsMapping[variant ?? 'grey'].text} />
                }
              />
            }
          >
            <Typography variant="label-medium" color={tailwindColors.white.DEFAULT}>
              {label}
            </Typography>
          </InformationToolTip>
        )
      }
    },
    {
      accessorKey: 'cseRegistration.cse.identifier',
      header: 'CSE',
      enableSorting: true,
      cell: ({ row }) => <CseIdentifierTooltip cse={row.original.cseRegistration?.cse} />
    },
    {
      accessorKey: 'origin',
      header: 'Origine',
      enableSorting: true,
      cell: ({ row }) => origineEnumMapping[row.original.origin].label
    },
    {
      accessorKey: 'status',
      header: 'Statut du Bulletin',
      enableSorting: true,
      cellClassName: 'whitespace-normal',
      cell: ({ row }) => (
        <Badge
          size="flexible"
          variant={applicationStatusEnumMapping[row.original.status].variant}
          text={applicationStatusEnumMapping[row.original.status].label}
        />
      )
    },
    {
      accessorKey: 'id',
      header: 'Source',
      cell: () => '-'
    },
    {
      accessorKey: 'attributedUser.firstName',
      header: 'Suivi par',
      enableSorting: true,
      width: 90,
      cellClassName: 'whitespace-normal',
      cell: ({ row }) => (
        <Typography variant="secondary-body" color={tailwindColors.blue.DEFAULT}>
          {toFullName(
            row.original.attributedUser?.firstName,
            row.original.attributedUser?.lastName
          )}
        </Typography>
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Demandes d’inscription à traiter" icon={FormIcon} />
      <div className="h-4" />
      <DynamicDataTable<EnrollmentListType, EnrollmentFilter>
        displayOrder
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        label={(count) => (
          <Badge
            text={`Total : ${count} (dont ${enrollmentsDistribution.normalIndividuals} individuels et ${enrollmentsDistribution.cse} CSE)`}
            variant="warning"
          />
        )}
        title="Base de transfert"
        // update the value of the badge when we know more about the CSE
        columns={columns.map((column) => ({
          ...column,
          cellClassName: cn('pl-2 pr-0', column.cellClassName),
          columnHeaderClassName: cn('pl-2 pr-0', column.columnHeaderClassName)
        }))}
        version="PARTIAL"
        deleteMessage={{ name: "bulletin d'inscription de", attributeName: 'childFirstName' }}
        selectable
        permissions={{ delete: false }}
        headerInfo={
          <div className="flex gap-2 flex-wrap">
            {displayStatuses.map((status) => {
              const isSatusOnlyInFilter =
                filter?.statuses?.length === 1 && filter.statuses.includes(status)
              return (
                <Badge
                  className={cn({ 'opacity-60': !isSatusOnlyInFilter })}
                  key={status}
                  text={`${applicationStatusEnumMapping[status].label}: ${statuses?.[status] ?? 0}`}
                  variant={applicationStatusEnumMapping[status].variant}
                  onClick={() => {
                    onSelectedFilterChange({
                      ...filter,
                      statuses: isSatusOnlyInFilter ? initialFilters.statuses : [status]
                    })
                  }}
                />
              )
            })}
          </div>
        }
        allowSearchParam
        extraParams={{ fields: enrollmentListFields }}
        endpoint={API.enrollments.getAll.url}
        onRowClick={(data, event) =>
          navigateWithEvent(Paths.ENROLLMENT_UPDATE_PAGE.replace(':id', data.id.toString()), event)
        }
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter, ['statuses'])}
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'multiple',
            icon: AddUserIcon,
            variant: 'ghost',
            label: 'Assigner à un Conseiller LEC',
            fn: (enrollments) => {
              toggleModal('assign', enrollments)
            }
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            variant: 'ghost',
            label: 'Exporter en CSV',
            fn: (hostFamilys) => {
              const ids = hostFamilys.map(({ id }) => id)
              downloadDocuments({ ids, endpoint: API.enrollments.exportExcel.url })
            }
          }
        ]}
      />
      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onReset={resetFilters}
        onFilterChange={onFilterChange}
        onClose={() => closeModel('filters')}
        onApply={() => onSelectedFilterChange({ ...filter })}
      >
        <EnrollmentsFilter version="enrollment" filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {/* Assign enrollments to a LEC advisor */}
      <Modal
        isOpen={modalState.assign}
        title="Assigner à un conseiller LEC"
        icon={AddUserIcon}
        hideActions
        onCancel={() => closeModel('assign')}
      >
        <AssignEnrollmentForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('assign'),
            onConfirm: async (values) => {
              if (!selectedData) return
              await mutateAsync({
                ids: selectedData.map(({ id }) => id),
                ...values
              })
              tableRef.current?.refetch()
              toggleModal('assign')
            }
          })}
        />
      </Modal>
    </div>
  )
}

export default EnrollmentsListPage
